import { useState, useEffect } from "react";
import {
  User,
  Network,
  Lock,
  ShieldAlert,
  ChevronRight,
  ArrowLeft,
  ChevronDown,
  Image as ImageIcon,
} from "lucide-react";
import { StepStepper } from "../../index.jsx";

const STEPS = [
  { step: 1, label: "Profile Details", desc: "Basic user information" },
  { step: 2, label: "Access & Assignment", desc: "Role and credentials" },
];

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-[#1d57a5] outline-none transition-all text-sm";
const labelCls =
  "block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5";

export default function UserOnboarding({ step, setStep, onFinish, companies, groups, selectedUserDetails }) {
  const isUpdate = !!selectedUserDetails;

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    FirstName: '',
    MiddleName: '',
    LastName: '',
    MobileNo: '',
    Email: '',
    Password: '',
    Address1: '',
    Address2: '',
    Address3: '',
    Pincode: '',
    GroupId: groups[0]?.groupId || '1',
    LoginID: '',
    CompanyId: '0',
    ProfilePhoto: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  useEffect(() => {
    if (selectedUserDetails) {
      setFormData({
        FirstName: selectedUserDetails.firstName || '',
        MiddleName: selectedUserDetails.middleName || '',
        LastName: selectedUserDetails.lastName || '',
        MobileNo: selectedUserDetails.mobileNo || '',
        Email: selectedUserDetails.email || '',
        Password: selectedUserDetails.passwordHash || '',
        Address1: selectedUserDetails.address1 || '',
        Address2: selectedUserDetails.address2 || '',
        Address3: selectedUserDetails.address3 || '',
        Pincode: selectedUserDetails.pincode || '',
        GroupId: selectedUserDetails.groupId || groups[0]?.groupId || '1',
        LoginID: selectedUserDetails.loginID || '',
        CompanyId: selectedUserDetails.companyId || '0',
        ProfilePhoto: selectedUserDetails.profilePhoto || null
      });
      if (selectedUserDetails.ProfilePhoto) {
        setPhotoPreview(selectedUserDetails.ProfilePhoto);
      }
    }
  }, [selectedUserDetails]);

  const validate = (step) => {
    const errs = {};
    if (step === 1) {
      if (!formData.FirstName.trim()) errs.FirstName = 'First Name is required';
      if (!formData.LastName.trim()) errs.LastName = 'Last Name is required';
      if (!formData.Email.trim()) errs.Email = 'Email is required';
      if (!formData.MobileNo.trim()) errs.MobileNo = 'Mobile No is required';
    }
    if (step === 2) {
      if (!formData.GroupId) errs.GroupId = 'Please select a role';
      if (!formData.LoginID.trim()) errs.LoginID = 'Login ID is required';
      if (!isUpdate && !formData.Password.trim()) errs.Password = 'Password is required';
    }
    return errs;
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'Email') updated.LoginID = value;
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, ProfilePhoto: file }));
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col md:flex-row overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ height: 'calc(100vh - 10rem)' }}>
      {/* Left Sidebar */}
      <div className="w-full md:w-80 flex-shrink-0 bg-slate-50 dark:bg-slate-900/40 border-r border-slate-100 dark:border-slate-700 p-8 sm:p-10 flex flex-col overflow-hidden">
        <div className="mb-10">
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">
            User Setup
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Provision workspace access
          </p>
        </div>
        <StepStepper steps={STEPS} currentStep={step} />
      </div>

      {/* Right Content */}
      <div className="flex-1 bg-white dark:bg-slate-800 relative overflow-x-hidden overflow-y-auto flex flex-col">
        <div className="flex-1 p-8 sm:p-12 overflow-y-auto hide-scrollbar">
          {/* ── Step 1: Profile Details ── */}
          {step === 1 && (
            <div className="max-w-2xl animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                  Profile Details
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Enter the personal and contact information for the new user.
                </p>
              </div>

              {/* Photo Upload */}
              <div className="flex items-center gap-6 mb-8">
                <label className="w-24 h-24 rounded-full border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-400 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer relative group overflow-hidden">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <User size={32} />
                  )}
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ImageIcon size={20} className="text-white" />
                  </div>
                  <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </label>
                <div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-white">
                    Profile Photo
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Optional. Recommended size 256×256px.
                  </p>
                </div>
              </div>

              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-5 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                <User size={16} className="text-[#1d57a5] dark:text-[#4a8ce3]" /> Personal Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>First Name <span className="text-red-500">*</span></label>
                  <input type="text" className={inputCls + (errors.FirstName ? ' border-red-400' : '')} value={formData.FirstName} onChange={(e) => handleChange('FirstName', e.target.value)} />
                  {errors.FirstName && <p className="text-red-500 text-xs mt-1">{errors.FirstName}</p>}
                </div>
                <div>
                  <label className={labelCls}>Middle Name</label>
                  <input type="text" className={inputCls} value={formData.MiddleName} onChange={(e) => handleChange('MiddleName', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Last Name <span className="text-red-500">*</span></label>
                  <input type="text" className={inputCls + (errors.LastName ? ' border-red-400' : '')} value={formData.LastName} onChange={(e) => handleChange('LastName', e.target.value)} />
                  {errors.LastName && <p className="text-red-500 text-xs mt-1">{errors.LastName}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    className={inputCls + (errors.Email ? ' border-red-400' : '')}
                    value={formData.Email}
                    onChange={(e) => handleChange('Email', e.target.value)}
                  />
                  {errors.Email && <p className="text-red-500 text-xs mt-1">{errors.Email}</p>}
                </div>
                <div>
                  <label className={labelCls}>Mobile No <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    className={inputCls + (errors.MobileNo ? ' border-red-400' : '')}
                    value={formData.MobileNo}
                    onChange={(e) => handleChange('MobileNo', e.target.value)}
                  />
                </div>
              </div>

              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-5 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                <Network size={16} className="text-[#36a852]" /> Address Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className={labelCls}>Address 1</label>
                  <input type="text" className={inputCls} value={formData.Address1} onChange={(e) => handleChange('Address1', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Address 2</label>
                  <input type="text" className={inputCls} value={formData.Address2} onChange={(e) => handleChange('Address2', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Address 3</label>
                  <input type="text" className={inputCls} value={formData.Address3} onChange={(e) => handleChange('Address3', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Pincode</label>
                  <input type="text" className={inputCls + " font-mono"} value={formData.Pincode} onChange={(e) => handleChange('Pincode', e.target.value)} />
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex justify-end">
                <button
                  onClick={() => {
                    const errs = validate(1);
                    if (Object.keys(errs).length) { setErrors(errs); return; }
                    setStep(2);
                  }}
                  className="bg-[#1d57a5] hover:bg-[#164280] text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2"
                >
                  Continue <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Access & Assignment ── */}
          {step === 2 && (
            <div className="max-w-2xl animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">Access &amp; Assignment</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Define the user's role and credentials.</p>
              </div>

              <div className="space-y-8">
                {/* Company assignment */}
                <div>
                  <label className={labelCls}>Company Assignment</label>
                  <div className="relative">
                    <select
                      value={formData.CompanyId}
                      onChange={(e) => handleChange('CompanyId', e.target.value)}
                      className={
                        inputCls + " py-3 appearance-none cursor-pointer"
                      }
                    >
                      <option value="0">
                        Select a tenant…
                      </option>
                      {companies.map((c) => (
                        <option key={c.companyID} value={c.companyID}>
                          {c.companyName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={16}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className={labelCls}>Role &amp; Permissions <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {groups.map((group) => (
                      <label key={group.groupId} className="flex items-start gap-4 p-4 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-[#1d57a5] dark:hover:border-[#4a8ce3] bg-white dark:bg-slate-800 cursor-pointer transition-colors group">
                        <input
                          type="radio"
                          name="role"
                          value={group.groupId}
                          className="mt-1 w-4 h-4 text-[#1d57a5] focus:ring-[#1d57a5] border-slate-300"
                          checked={formData.GroupId == group.groupId}
                          onChange={(e) => handleChange('GroupId', e.target.value)}
                        />
                        <h4 className="text-sm font-bold text-slate-800 dark:text-white mt-0.5 group-hover:text-[#1d57a5] dark:group-hover:text-[#4a8ce3] transition-colors">
                          {group.roleName}
                        </h4>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Security notice */}
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4 flex gap-3">
                  <ShieldAlert size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-bold text-amber-800 dark:text-amber-500 mb-1">Security Notice</h5>
                    <p className="text-xs text-amber-700/80 dark:text-amber-400/80 leading-relaxed">
                      An automated email will be sent with secure login instructions and a temporary password once provisioned.
                    </p>
                  </div>
                </div>

                {/* Credentials */}
                <div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-4 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                    <Lock size={16} className="text-purple-500" /> Credentials Setup
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Login ID <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        className={inputCls + (errors.LoginID ? ' border-red-400' : '')}
                        value={formData.LoginID}
                        onChange={(e) => handleChange('LoginID', e.target.value)}
                      />
                      {errors.LoginID && <p className="text-red-500 text-xs mt-1">{errors.LoginID}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>
                        Password {isUpdate && <span className="text-slate-400 font-normal">(optional)</span>}
                        {!isUpdate && <span className="text-red-500"> *</span>}
                      </label>
                      <input
                        type="password"
                        placeholder={isUpdate ? "Leave blank to keep current" : "••••••••"}
                        className={inputCls + (errors.Password ? ' border-red-400' : '')}
                        value={formData.Password}
                        onChange={(e) => handleChange('Password', e.target.value)}
                      />
                      {errors.Password && <p className="text-red-500 text-xs mt-1">{errors.Password}</p>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                >
                  <ArrowLeft size={16} /> Back
                </button>
                <button
                  onClick={() => {
                    const errs = validate(2);
                    if (Object.keys(errs).length) { setErrors(errs); return; }
                    onFinish(formData, isUpdate);
                  }}
                  className="bg-[#1d57a5] hover:bg-[#164280] text-white px-8 py-3 rounded-full font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2"
                >
                  {isUpdate ? 'Update User' : 'Provision User'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
